import React, { useEffect } from 'react';
import { Box, Button, Chip, Stack, Tab, Tabs, Typography } from '@mui/material';
import { type SxProps, type Theme } from '@mui/material/styles';
// Deep icon import: the @mui/icons-material barrel stalls vitest jsdom suites.
import ArrowBack from '@mui/icons-material/ArrowBack';
import { Link as RouterLink } from 'react-router-dom';
import {
  BORDER,
  CONTROL,
  INK,
  LAYOUT,
  RADIUS,
  STATUS_HEX,
  STATUS_INK,
  STATUS_TINT,
  TYPE,
  type StatusTone as FoundationTone,
} from '../foundation';
import { DS2_SPACE, DS2_TYPE } from './tokens';
import { MetricStrip, Overline } from './primitives';

type SxObject = Record<string, unknown>;
const asSx = (sx: SxProps<Theme> | undefined): SxObject => (sx ?? {}) as SxObject;

export const APP_NAME = 'EnsoMirai++';

/* ── usePageTitle ──────────────────────────────────────────────────── */

/** Sets `document.title` to "<title> · EnsoMirai++" while the page is mounted. */
export function usePageTitle(title: string | undefined | null): void {
  useEffect(() => {
    if (!title) return undefined;
    const previous = document.title;
    document.title = `${title} · ${APP_NAME}`;
    return () => {
      document.title = previous;
    };
  }, [title]);
}

/* ── Page ──────────────────────────────────────────────────────────── */

export interface PageProps {
  children: React.ReactNode;
  /** `default` caps at 1440px, `narrow` at 960px, `full` spans the workspace */
  width?: 'default' | 'narrow' | 'full';
  className?: string;
  sx?: SxProps<Theme>;
}

/**
 * Standard page container. The app shell already provides horizontal and
 * top padding; Page adds the max width and the vertical rhythm between
 * sections so every screen spaces its content the same way.
 */
export const Page: React.FC<PageProps> = ({ children, width = 'default', className, sx }) => (
  <Box
    className={className}
    sx={{
      width: '100%',
      maxWidth: width === 'full' ? 'none' : width === 'narrow' ? LAYOUT.narrowMaxWidth : LAYOUT.pageMaxWidth,
      mx: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: LAYOUT.sectionGap,
      pb: LAYOUT.pagePadBottom,
      minWidth: 0,
      '& > .ds2-page-header': { mb: 0 },
      ...asSx(sx),
    }}
  >
    {children}
  </Box>
);

/* ── PageHeader ────────────────────────────────────────────────────── */

export interface PageHeaderProps {
  /** Overline (eyebrow) above the title — use the nav section name */
  label?: string;
  title: string;
  /** One sentence explaining what this screen is for */
  description?: React.ReactNode;
  /** Status chips or key numbers shown under the title */
  metrics?: React.ReactNode;
  /** Primary / secondary actions, right-aligned */
  actions?: React.ReactNode;
  /** Optional back link rendered above the title */
  back?: { label: string; to: string };
  /** Set document.title (default true) */
  documentTitle?: boolean;
  sx?: SxProps<Theme>;
}

/** The one page header: h1 + optional overline, description and actions. */
export const PageHeader: React.FC<PageHeaderProps> = ({
  label,
  title,
  description,
  metrics,
  actions,
  back,
  documentTitle = true,
  sx,
}) => {
  usePageTitle(documentTitle ? title : null);
  return (
    <Box component="header" className="ds2-page-header" sx={{ mb: LAYOUT.headerGap, ...asSx(sx) }}>
      {back && (
        <Button
          component={RouterLink}
          to={back.to}
          size="small"
          startIcon={<ArrowBack fontSize="small" />}
          sx={{ ml: -1, mb: 1, color: 'text.secondary' }}
        >
          {back.label}
        </Button>
      )}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
          {label && <Overline sx={{ mb: 0.75 }}>{label}</Overline>}
          <Typography component="h1" sx={{ ...TYPE.pageTitle, color: 'text.primary', m: 0 }}>
            {title}
          </Typography>
          {description && (
            <Typography sx={{ ...TYPE.body, color: 'text.secondary', mt: 0.75, maxWidth: 820 }}>
              {description}
            </Typography>
          )}
          {metrics && (
            <Stack direction="row" flexWrap="wrap" useFlexGap gap={1} sx={{ mt: 1.5 }}>
              {metrics}
            </Stack>
          )}
        </Box>
        {actions && (
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            gap={1}
            sx={{ flexShrink: 0, justifyContent: 'flex-end' }}
          >
            {actions}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

/* ── Toolbar ───────────────────────────────────────────────────────── */

export interface ToolbarProps {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

export const Toolbar: React.FC<ToolbarProps> = ({ children, sx }) => (
  <Stack direction="row" flexWrap="wrap" alignItems="center" useFlexGap gap={1} sx={{ mb: 2, ...asSx(sx) }}>
    {children}
  </Stack>
);

/* ── ActionGroup ───────────────────────────────────────────────────── */

export interface ActionGroupProps {
  children: React.ReactNode;
  align?: 'start' | 'end';
}

/** Button row. Order: secondary actions first, the primary action last. */
export const ActionGroup: React.FC<ActionGroupProps> = ({ children, align = 'end' }) => (
  <Stack
    direction="row"
    flexWrap="wrap"
    useFlexGap
    gap={1}
    sx={{ justifyContent: align === 'end' ? 'flex-end' : 'flex-start' }}
  >
    {children}
  </Stack>
);

/* ── StatusBadge ───────────────────────────────────────────────────── */

/**
 * Status semantics (use these consistently):
 *  success — done, verified, healthy
 *  info    — in progress / running
 *  warning — waiting on someone, needs attention, medium risk
 *  error   — failed, blocked, critical / high risk
 *  policy  — held by governance policy
 *  active  — selected / current (accent)
 *  neutral — queued, idle, unknown, not started
 */
export type StatusTone = FoundationTone;

export interface StatusBadgeProps {
  label: React.ReactNode;
  tone?: StatusTone;
  /** Leading dot (default true) */
  dot?: boolean;
  /** Pulse the dot for live, in-flight states */
  pulse?: boolean;
  size?: 'sm' | 'md';
  onClick?: () => void;
  href?: string;
  component?: React.ElementType;
  to?: string;
  title?: string;
  sx?: SxProps<Theme>;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  label,
  tone = 'neutral',
  dot = true,
  pulse = false,
  size = 'sm',
  onClick,
  href,
  component,
  to,
  title,
  sx,
}) => {
  const clickable = Boolean(onClick || to || href);
  return (
    <Chip
      size="small"
      title={title}
      icon={
        dot ? (
          <Box
            component="span"
            aria-hidden
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: tone === 'neutral' ? INK.tertiary : STATUS_HEX[tone],
              flexShrink: 0,
              ...(pulse && {
                animation: 'ds2BadgePulse 2s ease-in-out infinite',
                '@keyframes ds2BadgePulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
              }),
            }}
          />
        ) : undefined
      }
      label={label}
      onClick={onClick}
      component={component ?? (to ? RouterLink : href ? 'a' : undefined)}
      to={to}
      href={href}
      clickable={clickable}
      sx={{
        height: size === 'md' ? CONTROL.chip.md : CONTROL.chip.sm,
        fontSize: size === 'md' ? '0.8125rem' : '0.75rem',
        fontWeight: 600,
        borderRadius: `${RADIUS.chip}px`,
        bgcolor: STATUS_TINT[tone],
        color: STATUS_INK[tone],
        border: 'none',
        maxWidth: '100%',
        '& .MuiChip-icon': { ml: size === 'md' ? 1.25 : 1, mr: -0.25 },
        '& .MuiChip-label': { px: size === 'md' ? 1.25 : 1 },
        ...(clickable && {
          '&:hover': { bgcolor: STATUS_TINT[tone], filter: 'brightness(0.97)' },
        }),
        ...asSx(sx),
      }}
    />
  );
};

/* ── KpiRow ────────────────────────────────────────────────────────── */

export interface KpiRowProps {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

export const KpiRow: React.FC<KpiRowProps> = ({ children, sx }) => (
  <MetricStrip sx={{ mb: DS2_SPACE.lg, ...asSx(sx) }}>{children}</MetricStrip>
);

/* ── SubNav ────────────────────────────────────────────────────────── */

export interface SubNavItem<T extends string = string> {
  value: T;
  label: React.ReactNode;
  icon?: React.ReactElement;
  disabled?: boolean;
  title?: string;
}

export interface SubNavProps<T extends string = string> {
  items: SubNavItem<T>[];
  value: T | false;
  onChange: (value: T) => void;
  /** Required: names the navigation for screen readers */
  ariaLabel: string;
  /** `primary` for section tabs, `secondary` for a nested level */
  level?: 'primary' | 'secondary';
  /** Indicator / active label color; defaults to the accent */
  color?: string;
  /** Hairline under the tab row */
  divider?: boolean;
  sx?: SxProps<Theme>;
}

/** The one tab bar. Underline indicator, sentence-case labels, scrollable. */
export function SubNav<T extends string = string>({
  items,
  value,
  onChange,
  ariaLabel,
  level = 'primary',
  color,
  divider = true,
  sx,
}: SubNavProps<T>): React.ReactElement {
  const secondary = level === 'secondary';
  const height = secondary ? 36 : CONTROL.tab;
  return (
    <Tabs
      value={value}
      onChange={(_, v: T) => onChange(v)}
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      aria-label={ariaLabel}
      sx={{
        minHeight: height,
        ...(divider && { borderBottom: `1px solid ${BORDER.subtle}` }),
        '& .MuiTabs-indicator': {
          height: 2,
          bgcolor: color ?? 'var(--accent)',
        },
        '& .MuiTab-root': {
          minHeight: height,
          py: secondary ? 0.75 : 1.25,
          px: secondary ? 1 : 1.5,
          fontSize: secondary ? '0.8125rem' : '0.875rem',
          '& svg': { width: secondary ? 14 : 16, height: secondary ? 14 : 16, fontSize: secondary ? 14 : 16 },
        },
        '& .MuiTab-root.Mui-selected': {
          color: 'text.primary',
        },
        ...asSx(sx),
      }}
    >
      {items.map((item) => (
        <Tab
          key={item.value}
          value={item.value}
          label={item.label}
          icon={item.icon}
          iconPosition="start"
          disabled={item.disabled}
          title={item.title}
        />
      ))}
    </Tabs>
  );
}

/* ── Timeline ──────────────────────────────────────────────────────── */

export interface TimelineItem {
  id: string;
  label: string;
  detail?: string;
  time?: string;
  tone?: StatusTone;
}

export interface TimelineProps {
  items: TimelineItem[];
  maxItems?: number;
}

export const Timeline: React.FC<TimelineProps> = ({ items, maxItems }) => {
  const visible = maxItems ? items.slice(0, maxItems) : items;
  return (
    <Stack component="ol" spacing={0} sx={{ position: 'relative', listStyle: 'none', m: 0, p: 0 }}>
      {visible.map((item, idx) => {
        const tone = item.tone ?? 'neutral';
        const isLast = idx === visible.length - 1;
        return (
          <Stack component="li" key={item.id} direction="row" spacing={1.5} sx={{ pb: isLast ? 0 : 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.75 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: tone === 'neutral' ? INK.tertiary : STATUS_HEX[tone],
                  flexShrink: 0,
                }}
              />
              {!isLast && <Box sx={{ width: '1px', flex: 1, minHeight: 16, bgcolor: BORDER.default, mt: 0.5 }} />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {item.label}
                </Typography>
                {item.time && (
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {item.time}
                  </Typography>
                )}
              </Stack>
              {item.detail && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  {item.detail}
                </Typography>
              )}
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
};

/* ── InspectorEntry ────────────────────────────────────────────────── */

export interface InspectorEntryProps {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  action?: React.ReactNode;
}

export const InspectorEntry: React.FC<InspectorEntryProps> = ({ label, value, mono, action }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 2,
      py: 1.25,
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography component="div" sx={{ ...DS2_TYPE.label, mb: 0.25 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{
          fontWeight: 500,
          wordBreak: 'break-word',
          ...(mono && { ...TYPE.mono }),
        }}
      >
        {value}
      </Typography>
    </Box>
    {action}
  </Box>
);

/* Re-export Button for toolbar consistency */
export { Button };
